import { useState, useEffect, useCallback } from "react";
import type { HighSchoolData } from "../utils/storage";
import {
  setHighSchoolData,
  getHighSchoolData,
  clearHighSchoolData,
} from "../utils/storage";

interface UseHighSchoolSelectionProps {
  initialCeeb?: string | number | null;
  onSelectionChange?: (data: HighSchoolData | null) => void;
  autoLoadFromStorage?: boolean;
}

interface UseHighSchoolSelectionReturn {
  selectedSchool: HighSchoolData | null;
  displayName: string;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  selectSchool: (ceeb: number, hsName: string) => void;
  clearSelection: () => void;
  fetchSchoolByCeeb: (ceeb: string | number) => Promise<HighSchoolData | null>;
  searchSchoolsByText: (searchText: string) => Promise<HighSchoolData[]>;
  hasUserSelected: boolean;
}
const API_BASE_URL = "https://dev.netpriceestimate.com";

/**
 * Custom hook to manage high school selection with localStorage persistence
 * By default, does NOT auto-load from localStorage on mount to keep field blank initially
 */
export const useHighSchoolSelection = ({
  initialCeeb,
  onSelectionChange,
  autoLoadFromStorage = false,
}: UseHighSchoolSelectionProps = {}): UseHighSchoolSelectionReturn => {
  const [selectedSchool, setSelectedSchool] = useState<HighSchoolData | null>(
    null,
  );
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Load from localStorage only if explicitly enabled OR if initialCeeb is provided (navigating back)
  // This effect intentionally runs only once on mount to initialize state from localStorage
  // autoLoadFromStorage, initialCeeb, and onSelectionChange are captured in the closure
  // and should not trigger re-initialization when they change
  useEffect(() => {
    const storedData = getHighSchoolData();

    const shouldLoadFromStorage =
      (autoLoadFromStorage && storedData) ||
      (initialCeeb &&
        storedData &&
        String(storedData.ceeb) === String(initialCeeb));

    if (shouldLoadFromStorage && storedData) {
      // User is either explicitly loading or navigating back with a form value
      setSelectedSchool(storedData);
      setHasUserSelected(true);
      onSelectionChange?.(storedData);
    } else if (!autoLoadFromStorage && !initialCeeb) {
      // True initial page load - clear localStorage to ensure blank state
      clearHighSchoolData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Select a high school and persist to localStorage
   * Marks that user has made a selection
   */
  const selectSchool = useCallback(
    (ceeb: number, hsName: string) => {
      const data: HighSchoolData = { ceeb, hsName };
      setSelectedSchool(data);
      setHasUserSelected(true);
      setHighSchoolData(data);
      onSelectionChange?.(data);
    },
    [onSelectionChange],
  );

  /**
   * Clear the selected school from state and localStorage
   */
  const clearSelection = useCallback(() => {
    setSelectedSchool(null);
    setHasUserSelected(false);
    clearHighSchoolData();
    onSelectionChange?.(null);
  }, [onSelectionChange]);

  /**
   * Fetch high school data by CEEB code
   */
  const fetchSchoolByCeeb = useCallback(
    async (ceeb: string | number): Promise<HighSchoolData | null> => {
      setIsLoading(true);
      setIsError(false);
      setError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/highschools?schooltext=${ceeb}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch high school data");
        }

        const data: HighSchoolData[] = await response.json();
        const schoolData = data[0] || null;

        if (schoolData) {
          selectSchool(schoolData.ceeb, schoolData.hsName);
        }

        return schoolData;
      } catch (err) {
        const errorObj =
          err instanceof Error ? err : new Error("Unknown error");
        console.error("Error fetching high school by CEEB:", errorObj);
        setIsError(true);
        setError(errorObj);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [selectSchool],
  );

  // Fetch by CEEB if provided but not in localStorage (edge case)
  useEffect(() => {
    if (initialCeeb && !selectedSchool && hasUserSelected) {
      fetchSchoolByCeeb(initialCeeb);
    }
  }, [initialCeeb, hasUserSelected, selectedSchool, fetchSchoolByCeeb]);

  /**
   * Search high schools by text
   */
  const searchSchoolsByText = useCallback(
    async (searchText: string): Promise<HighSchoolData[]> => {
      if (!searchText || searchText.length < 2) {
        return [];
      }

      setIsLoading(true);
      setIsError(false);
      setError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/highschools?schooltext=${encodeURIComponent(searchText)}`,
        );

        if (!response.ok) {
          throw new Error("Failed to search high schools");
        }

        const data: HighSchoolData[] = await response.json();
        return data;
      } catch (err) {
        const errorObj =
          err instanceof Error ? err : new Error("Unknown error");
        console.error("Error searching high schools:", errorObj);
        setIsError(true);
        setError(errorObj);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const displayName = selectedSchool?.hsName || "";

  return {
    selectedSchool,
    displayName,
    isLoading,
    isError,
    error,
    selectSchool,
    clearSelection,
    fetchSchoolByCeeb,
    searchSchoolsByText,
    hasUserSelected,
  };
};
